import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Users, Pencil, Copy, Mail, Clock, MapPin, User, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { NativeSelect, Field, StatusChip, ConfirmDialog } from "../common";
import { EleveMultiSelect } from "./EleveMultiSelect";
import { DuplicateDialog } from "./DuplicateDialog";
import { fmtDateLong, fmtDuration, addMinutesToTime } from "../../lib/format";

function reportResult(res) {
  (res.warnings || []).forEach((w) => toast.warning(w, { duration: 8000 }));
  if (res.modules_ajoutes) toast.info(`Module ajouté à ${res.modules_ajoutes} fiche${res.modules_ajoutes > 1 ? "s" : ""} élève`);
  const emails = res.emails || [];
  if (emails.length) {
    const sent = emails.filter((e) => e.sent).length;
    if (sent) toast.success(`${sent} convocation${sent > 1 ? "s" : ""} envoyée${sent > 1 ? "s" : ""} par e-mail`);
    const failed = emails.filter((e) => !e.sent);
    if (failed.length) toast.warning(`E-mail non envoyé à : ${failed.map((f) => `${f.nom} (${f.error})`).join(", ")}`, { duration: 9000 });
  }
}

function useFormateurs(open) {
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/users").then(({ data }) => setList(data.filter((u) => u.user_type !== "eleve" && u.active !== false))).catch(() => setList([]));
  }, [open]);
  return list;
}

// ---------------------------------------------------------------------------
// Ajout d'un cours sur une journée : module + heure de début ; l'heure de fin
// se remplit toute seule avec la durée du module (modifiable).
// ---------------------------------------------------------------------------
export function AddSessionDialog({ open, onOpenChange, date, defaultTime = "09:00", modules = [], eleves = [], currentUser, onCreated }) {
  const formateurs = useFormateurs(open);
  const [moduleId, setModuleId] = useState("");
  const [debut, setDebut] = useState(defaultTime);
  const [fin, setFin] = useState("");
  const [finTouched, setFinTouched] = useState(false);
  const [formateurId, setFormateurId] = useState("");
  const [salle, setSalle] = useState("");
  const [commentaire, setCommentaire] = useState("");
  const [eleveIds, setEleveIds] = useState([]);
  const [addToFiche, setAddToFiche] = useState(true);
  const [notify, setNotify] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setModuleId(""); setDebut(defaultTime); setFin(""); setFinTouched(false); setFormateurId(currentUser?.id || "");
    setSalle(""); setCommentaire(""); setEleveIds([]); setAddToFiche(true); setNotify(false);
  }, [open, defaultTime, currentUser]);

  const module = modules.find((m) => m.id === moduleId);
  useEffect(() => {
    if (finTouched) return;
    if (module?.duree_minutes && debut) setFin(addMinutesToTime(debut, module.duree_minutes));
    else if (module && !module.duree_minutes) setFin("");
  }, [module, debut, finTouched]);

  const submit = async (e) => {
    e.preventDefault();
    if (!moduleId) return toast.error("Choisissez un module");
    if (!fin) return toast.error("Indiquez l'heure de fin (ce module n'a pas de durée configurée)");
    setSaving(true);
    try {
      const { data } = await api.post("/planning/sessions", {
        date, module_id: moduleId, heure_debut: debut, heure_fin: fin, formateur_id: formateurId || null,
        salle, commentaire, eleve_ids: eleveIds, add_module_to_fiche: addToFiche, notify,
      });
      toast.success("Cours ajouté au planning");
      reportResult(data);
      onCreated && onCreated(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Ajouter un module au planning</DialogTitle>
          <DialogDescription className="capitalize">{date ? fmtDateLong(date) : ""}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="add-session-form">
          <Field label="Module" required>
            <NativeSelect value={moduleId} onChange={(e) => setModuleId(e.target.value)} data-testid="add-session-module" autoFocus>
              <option value="">— Choisir un module —</option>
              {modules.map((m) => (
                <option key={m.id} value={m.id}>{m.nom}{m.duree_minutes ? ` (${fmtDuration(m.duree_minutes)})` : ""}</option>
              ))}
            </NativeSelect>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Heure de début" required>
              <Input type="time" value={debut} onChange={(e) => setDebut(e.target.value)} required data-testid="add-session-debut" />
            </Field>
            <Field
              label="Heure de fin"
              required
              hint={module?.duree_minutes ? (finTouched ? "Modifiée manuellement" : `Calculée avec la durée du module (${fmtDuration(module.duree_minutes)})`) : module ? "Ce module n'a pas de durée : saisissez la fin" : ""}
            >
              <Input type="time" value={fin} onChange={(e) => { setFin(e.target.value); setFinTouched(true); }} required data-testid="add-session-fin" />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Formateur">
              <NativeSelect value={formateurId} onChange={(e) => setFormateurId(e.target.value)}>
                {formateurs.map((f) => <option key={f.id} value={f.id}>{f.prenom} {f.nom}</option>)}
                {!formateurs.length && currentUser && <option value={currentUser.id}>{currentUser.prenom} {currentUser.nom}</option>}
              </NativeSelect>
            </Field>
            <Field label="Salle / lieu (facultatif)">
              <Input value={salle} onChange={(e) => setSalle(e.target.value)} placeholder="Atelier 1" />
            </Field>
          </div>
          <Field label="Commentaire (facultatif)">
            <Textarea rows={2} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
          </Field>
          <div>
            <p className="text-sm font-medium mb-1.5">Élèves (facultatif — vous pourrez aussi les ajouter ensuite)</p>
            <EleveMultiSelect eleves={eleves} value={eleveIds} onChange={setEleveIds} maxHeight={180} />
            {eleveIds.length > 0 && (
              <div className="mt-2 space-y-1.5">
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={addToFiche} onCheckedChange={(v) => setAddToFiche(!!v)} /> Ajouter le module à la fiche des élèves</label>
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={notify} onCheckedChange={(v) => setNotify(!!v)} /> Prévenir les élèves par e-mail</label>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={saving} data-testid="add-session-submit">Ajouter au planning</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Détail d'un cours : élèves inscrits (ajout / retrait), modification,
// duplication, suppression.
// ---------------------------------------------------------------------------
export function SessionDialog({ open, onOpenChange, session, canManage, modules = [], eleves = [], onChanged, initialTab = "eleves" }) {
  const formateurs = useFormateurs(open && canManage);
  const [tab, setTab] = useState(initialTab);
  const [eleveIds, setEleveIds] = useState([]);
  const [addToFiche, setAddToFiche] = useState(true);
  const [notify, setNotify] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);

  useEffect(() => {
    if (!open || !session) return;
    setTab(initialTab);
    setEleveIds(session.eleve_ids || []);
    setAddToFiche(true);
    setNotify(false);
    setForm({ date: session.date, module_id: session.module_id, heure_debut: session.heure_debut, heure_fin: session.heure_fin,
      formateur_id: session.formateur_id || "", salle: session.salle || "", commentaire: session.commentaire || "" });
  }, [open, session, initialTab]);

  const dirtyEleves = useMemo(() => {
    if (!session) return false;
    const a = [...(session.eleve_ids || [])].sort().join(",");
    const b = [...eleveIds].sort().join(",");
    return a !== b;
  }, [session, eleveIds]);
  const newlyAdded = session ? eleveIds.filter((i) => !(session.eleve_ids || []).includes(i)) : [];

  if (!session) return null;

  const saveEleves = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/planning/sessions/${session.id}/eleves`, { eleve_ids: eleveIds, add_module_to_fiche: addToFiche, notify });
      toast.success("Élèves du cours mis à jour — leur planning est à jour");
      reportResult(data);
      onChanged && onChanged(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveDetails = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch(`/planning/sessions/${session.id}`, { ...form, formateur_id: form.formateur_id || null });
      toast.success("Cours modifié");
      reportResult(data);
      onChanged && onChanged(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await api.delete(`/planning/sessions/${session.id}`);
      toast.success("Cours supprimé du planning");
      setConfirmDelete(false);
      onOpenChange(false);
      onChanged && onChanged(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const changeModule = (id) => {
    const m = modules.find((x) => x.id === id);
    setForm((f) => ({ ...f, module_id: id, heure_fin: m?.duree_minutes ? addMinutesToTime(f.heure_debut, m.duree_minutes) : f.heure_fin }));
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="pr-6">{session.module_nom}</DialogTitle>
            <DialogDescription asChild>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="capitalize">{fmtDateLong(session.date)}</span>
                <span className="flex items-center gap-1 tabular-nums"><Clock size={13} />{session.heure_debut} – {session.heure_fin}</span>
                {session.formateur_nom && <span className="flex items-center gap-1"><User size={13} />{session.formateur_nom}</span>}
                {session.salle && <span className="flex items-center gap-1"><MapPin size={13} />{session.salle}</span>}
                <StatusChip statut={session.statut} />
              </div>
            </DialogDescription>
          </DialogHeader>

          {session.commentaire && <p className="text-sm rounded-md bg-muted px-3 py-2">{session.commentaire}</p>}

          {!canManage ? (
            <div>
              <p className="text-sm font-medium mb-2 flex items-center gap-1.5"><Users size={15} /> Élèves inscrits ({session.eleves?.length || 0})</p>
              <div className="flex flex-wrap gap-1.5">
                {(session.eleves || []).map((e) => <span key={e.id} className="rounded-full bg-muted px-2.5 py-1 text-xs">{e.prenom} {e.nom}</span>)}
                {!session.eleves?.length && <span className="text-sm text-muted-foreground">Aucun élève</span>}
              </div>
            </div>
          ) : (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="w-full grid grid-cols-3">
                <TabsTrigger value="eleves" className="gap-1.5"><Users size={14} /> Élèves</TabsTrigger>
                <TabsTrigger value="details" className="gap-1.5"><Pencil size={14} /> Modifier</TabsTrigger>
                <TabsTrigger value="autres" className="gap-1.5"><Copy size={14} /> Plus</TabsTrigger>
              </TabsList>

              <TabsContent value="eleves" className="space-y-3 mt-4">
                <EleveMultiSelect eleves={eleves} value={eleveIds} onChange={setEleveIds} />
                {newlyAdded.length > 0 && (
                  <div className="space-y-1.5 rounded-md bg-muted/50 p-2.5">
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={addToFiche} onCheckedChange={(v) => setAddToFiche(!!v)} /> Ajouter « {session.module_nom} » à la fiche des nouveaux élèves</label>
                    <label className="flex items-center gap-2 text-sm"><Checkbox checked={notify} onCheckedChange={(v) => setNotify(!!v)} /><Mail size={14} /> Prévenir les nouveaux élèves par e-mail</label>
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {(session.eleves || []).map((e) => (
                    <Link key={e.id} to={`/eleves/${e.id}`} className="rounded-full border border-border px-2.5 py-1 text-xs hover:bg-accent">{e.prenom} {e.nom}</Link>
                  ))}
                </div>
                <DialogFooter>
                  <Button onClick={saveEleves} disabled={!dirtyEleves || saving} data-testid="session-save-eleves">Enregistrer les élèves</Button>
                </DialogFooter>
              </TabsContent>

              <TabsContent value="details" className="mt-4">
                <form onSubmit={saveDetails} className="space-y-3">
                  <Field label="Module">
                    <NativeSelect value={form.module_id} onChange={(e) => changeModule(e.target.value)}>
                      {modules.map((m) => <option key={m.id} value={m.id}>{m.nom}</option>)}
                      {!modules.some((m) => m.id === form.module_id) && <option value={form.module_id}>{session.module_nom}</option>}
                    </NativeSelect>
                  </Field>
                  <div className="grid grid-cols-3 gap-3">
                    <Field label="Date"><Input type="date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} required /></Field>
                    <Field label="Début"><Input type="time" value={form.heure_debut || ""} onChange={(e) => setForm({ ...form, heure_debut: e.target.value })} required /></Field>
                    <Field label="Fin"><Input type="time" value={form.heure_fin || ""} onChange={(e) => setForm({ ...form, heure_fin: e.target.value })} required /></Field>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Formateur">
                      <NativeSelect value={form.formateur_id} onChange={(e) => setForm({ ...form, formateur_id: e.target.value })}>
                        {formateurs.map((f) => <option key={f.id} value={f.id}>{f.prenom} {f.nom}</option>)}
                        {!formateurs.some((f) => f.id === form.formateur_id) && <option value={form.formateur_id}>{session.formateur_nom}</option>}
                      </NativeSelect>
                    </Field>
                    <Field label="Salle / lieu"><Input value={form.salle || ""} onChange={(e) => setForm({ ...form, salle: e.target.value })} /></Field>
                  </div>
                  <Field label="Commentaire"><Textarea rows={2} value={form.commentaire || ""} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} /></Field>
                  <DialogFooter><Button type="submit" disabled={saving}>Enregistrer</Button></DialogFooter>
                </form>
              </TabsContent>

              <TabsContent value="autres" className="mt-4 space-y-5">
                <div>
                  <p className="text-sm font-medium mb-1.5">Dupliquer ce cours</p>
                  <p className="text-xs text-muted-foreground mb-2">Même module, mêmes horaires, même formateur (et les élèves si vous le souhaitez), vers une autre date ou une plage de dates.</p>
                  <Button variant="outline" onClick={() => setDupOpen(true)} data-testid="session-duplicate"><Copy size={14} /> Dupliquer…</Button>
                </div>
                <div className="rounded-lg border border-destructive/30 p-3">
                  <p className="text-sm font-medium flex items-center gap-1.5"><AlertTriangle size={15} className="text-destructive" /> Supprimer ce cours</p>
                  <p className="text-xs text-muted-foreground mt-1 mb-2">Le cours disparaît du planning de tous les élèves inscrits. Les fiches élèves (modules, notes) ne sont pas modifiées.</p>
                  <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(true)} data-testid="session-delete"><Trash2 size={14} /> Supprimer</Button>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
      <DuplicateDialog open={dupOpen} onOpenChange={setDupOpen} mode="session" sourceDate={session.date} session={session} onDone={() => onChanged && onChanged(session)} />
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Supprimer ce cours ?"
        description={`${session.module_nom} — ${session.heure_debut}-${session.heure_fin}. Cette action est irréversible.`}
        confirmLabel="Supprimer"
        onConfirm={remove}
        loading={saving}
      />
    </>
  );
}
